import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Table,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { Coins, Plus, Minus } from "lucide-react";
import toast from "react-hot-toast";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import { formatDate } from "@/utils/date";
import {
  useUserBalances,
  useUserBalanceHistory,
  useAdjustUserBalance,
} from "@/hooks/useAnythinkPayOffers";

type Direction = "grant" | "deduct";

type AdjustState = {
  direction: Direction;
  unitType: "points" | "credit";
  currency: string;
  amount: string;
  reason: string;
};

const emptyAdjust = (): AdjustState => ({
  direction: "grant",
  unitType: "points",
  currency: "gbp",
  amount: "",
  reason: "",
});

export default function UserBalanceCard({ userId }: { userId: number }) {
  const { data: balances, isLoading: balancesLoading } = useUserBalances(userId);
  const { data: history, isLoading: historyLoading } = useUserBalanceHistory(userId, 1, 25);
  const adjust = useAdjustUserBalance(userId);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<AdjustState>(emptyAdjust());

  const onAdjust = async () => {
    const amount = parseFloat(state.amount.trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a positive amount");
      return;
    }

    const sign = state.direction === "grant" ? 1 : -1;
    const delta = state.unitType === "credit"
      ? sign * Math.round(amount * 100)
      : sign * Math.round(amount);

    const trimmedReason = state.reason.trim();
    try {
      await adjust.mutateAsync({
        unit_type: state.unitType,
        currency: state.unitType === "credit" ? state.currency.toLowerCase() : null,
        delta,
        reason: trimmedReason ? trimmedReason : null,
      });
      setOpen(false);
      setState(emptyAdjust());
    } catch {
      // toast handled by service
    }
  };

  return (
    <Card>
      <Box p="2">
        <Flex justify="between" align="center" mb="4">
          <Flex align="center" gap="2">
            <Coins size={18} />
            <Heading size="4" weight="medium">Balance</Heading>
          </Flex>
          <Button variant="soft" size="2" onClick={() => setOpen(true)}>
            Adjust balance
          </Button>
        </Flex>

        {balancesLoading ? (
          <Text size="2" color="gray">Loading balances…</Text>
        ) : balances && balances.length > 0 ? (
          <Grid columns={{ initial: "1", sm: "2", md: "3" }} gap="3" mb="5">
            {balances.map((b) => (
              <Card key={`${b.unit_type}:${b.currency ?? ""}`} variant="surface">
                <Flex direction="column" gap="1" p="2">
                  <Text size="1" color="gray">
                    {b.unit_type === "points" ? "Points" : `Credit ${b.currency?.toUpperCase() ?? ""}`.trim()}
                  </Text>
                  <Text size="6" weight="bold">
                    {b.unit_type === "credit"
                      ? `${(b.total / 100).toFixed(2)} ${b.currency?.toUpperCase() ?? ""}`.trim()
                      : b.total.toLocaleString()}
                  </Text>
                </Flex>
              </Card>
            ))}
          </Grid>
        ) : (
          <Text size="2" color="gray" as="p" mb="5">
            This user has no balance yet. Grant points or credit using the button above.
          </Text>
        )}

        <Heading size="2" color="gray" weight="medium" mb="2">
          History
        </Heading>
        {historyLoading ? (
          <Text size="2" color="gray">Loading history…</Text>
        ) : history && history.length > 0 ? (
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Change</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>By</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {history.map((e) => {
                const display = e.unit_type === "credit"
                  ? `${e.delta >= 0 ? "+" : ""}${(e.delta / 100).toFixed(2)} ${e.currency?.toUpperCase() ?? ""}`.trim()
                  : `${e.delta >= 0 ? "+" : ""}${e.delta} pts`;
                return (
                  <Table.Row key={e.id}>
                    <Table.Cell>
                      <Text size="2" color="gray">{formatDate(e.created_at)}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge size="1" variant="soft" color={e.delta >= 0 ? "green" : "red"}>
                        {display}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">{e.source.replace(/_/g, " ")}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">{e.reason ?? "—"}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text size="2">
                        {e.created_by
                          ? `${e.created_by.first_name} ${e.created_by.last_name}`.trim()
                          : e.created_by_user_id
                          ? `#${e.created_by_user_id}`
                          : "System"}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        ) : (
          <Text size="2" color="gray">No balance changes recorded.</Text>
        )}
      </Box>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Adjust user balance"
        size="md"
        modal={false}
        actions={
          <Flex gap="2" justify="end">
            <Button variant="soft" color="gray" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onAdjust} loading={adjust.isPending}>
              {state.direction === "grant" ? (
                <>
                  <Plus size={14} /> Grant
                </>
              ) : (
                <>
                  <Minus size={14} /> Deduct
                </>
              )}
            </Button>
          </Flex>
        }
      >
        <Flex direction="column" gap="4" p="2">
          <Grid columns="2" gap="3">
            <Box>
              <Text size="2" weight="medium" as="div" mb="1">Direction</Text>
              <Select
                value={state.direction}
                onChange={(v) => setState({ ...state, direction: v as Direction })}
                options={[
                  { value: "grant", label: "Grant (+)" },
                  { value: "deduct", label: "Deduct (−)" },
                ]}
              />
            </Box>
            <Box>
              <Text size="2" weight="medium" as="div" mb="1">Unit</Text>
              <Select
                value={state.unitType}
                onChange={(v) =>
                  setState({ ...state, unitType: v as "points" | "credit" })
                }
                options={[
                  { value: "points", label: "Points" },
                  { value: "credit", label: "Account credit" },
                ]}
              />
            </Box>
          </Grid>

          {state.unitType === "credit" && (
            <Box style={{ maxWidth: 160 }}>
              <Text size="2" weight="medium" as="div" mb="1">Currency</Text>
              <Select
                value={state.currency}
                onChange={(v) => setState({ ...state, currency: v })}
                options={[
                  { value: "gbp", label: "GBP" },
                  { value: "usd", label: "USD" },
                  { value: "eur", label: "EUR" },
                ]}
              />
            </Box>
          )}

          <Box>
            <Text size="2" weight="medium" as="div" mb="1">
              {state.unitType === "credit"
                ? `Amount (${state.currency.toUpperCase()})`
                : "Amount (points)"}
            </Text>
            <TextField.Root
              type="number"
              min="0"
              step={state.unitType === "credit" ? "0.01" : "1"}
              value={state.amount}
              onChange={(e) => setState({ ...state, amount: e.target.value })}
              placeholder={state.unitType === "credit" ? "5.00" : "100"}
            />
          </Box>

          <Box>
            <Text size="2" weight="medium" as="div" mb="1">
              Reason <Text size="1" color="gray">(optional)</Text>
            </Text>
            <TextArea
              value={state.reason}
              onChange={(e) => setState({ ...state, reason: e.target.value })}
              placeholder="Goodwill credit for support ticket #1234"
              rows={2}
            />
            <Text size="1" color="gray" as="div" mt="1">
              Recorded against the entry and visible to other admins.
            </Text>
          </Box>
        </Flex>
      </Modal>
    </Card>
  );
}
